import { useState, useCallback } from 'react';
import { AlertTriangle, Check, Power, Zap, ListChecks, FlaskConical } from 'lucide-react';
import InfoBox from '../InfoBox';
import { GasDischargeSetup } from '@/components/lab';

const steps = [
  { id: 1, title: 'Safety Acknowledgment', short: 'Review and accept safety warnings' },
  { id: 2, title: 'Select Discharge Tube', short: 'Choose the hydrogen tube' },
  { id: 3, title: 'Power On', short: 'Turn on the power supply' }
];

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'High Voltage Safety',
    content: 'Gas discharge tubes require high voltage (5000V) to excite gas atoms. LAMP MAY BECOME HOT DURING USE - allow it to cool for a few minutes before switching it out. ONLY handle the bulbs by the larger ends, not the slim centre piece. Lift the spectrometer using the base only, not the arms, and keep it level in transport. Use a wood clamp to secure the spectrometer to the bench.'
  },
  2: {
    title: 'Hydrogen Discharge Tube',
    content: 'Hydrogen (H) is the simplest atom with one electron. When excited, its electron transitions produce the famous Balmer series - discrete spectral lines in the visible range. The pink-purple glow comes from the combination of red (H-alpha at 656 nm), cyan (H-beta at 486 nm), and violet lines.'
  },
  3: {
    title: 'Gas Excitation',
    content: 'When voltage is applied, electrons in the gas gain kinetic energy. Upon collision with gas atoms, this energy promotes electrons to higher energy levels. As these excited electrons relax back to lower energy states, they emit photons with specific wavelengths determined by the energy difference: E = hc/λ.'
  }
};

type GasType = 'hydrogen' | 'helium' | 'neon';

interface Part1SetupProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
  onStateChange?: (state: { selectedGas: GasType | null; isPowered: boolean }) => void;
}

export default function Part1Setup({ onStepComplete, currentStep = 1, onStateChange }: Part1SetupProps) {
  const [safetyAcknowledged, setSafetyAcknowledged] = useState(false);
  const [selectedGas, setSelectedGas] = useState<GasType | null>(null);
  const [isPowered, setIsPowered] = useState(false);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const stepDone = [safetyAcknowledged, !!selectedGas, isPowered];
  const completedCount = stepDone.filter(Boolean).length;
  const activeStep = completedCount < steps.length ? completedCount + 1 : steps.length;

  const handleAcknowledgeSafety = useCallback(() => {
    setSafetyAcknowledged(true);
    if (onStepComplete) onStepComplete('setup-1');
  }, [onStepComplete]);

  const handleSelectTube = useCallback((tube: GasType) => {
    if (safetyAcknowledged && !isPowered) {
      setSelectedGas(tube);
      if (onStateChange) onStateChange({ selectedGas: tube, isPowered: false });
      if (onStepComplete) onStepComplete('setup-2');
    }
  }, [safetyAcknowledged, isPowered, onStepComplete, onStateChange]);

  const handleTogglePower = useCallback(() => {
    if (selectedGas) {
      const newPowered = !isPowered;
      setIsPowered(newPowered);
      if (onStateChange) onStateChange({ selectedGas, isPowered: newPowered });
      if (newPowered && onStepComplete) onStepComplete('setup-3');
    }
  }, [selectedGas, isPowered, onStepComplete, onStateChange]);

  return (
    <div className="exp1-part-layout">
      {/* Steps Panel */}
      <div className="exp1-steps-panel">
        <div className="exp1-steps-header">
          <ListChecks size={16} />
          <h3>Part I: Setup</h3>
        </div>
        <div className="exp1-steps-list">
          {steps.map(s => {
            const done = stepDone[s.id - 1];
            const active = s.id === activeStep;
            return (
              <div key={s.id} className={`exp1-step-item ${active && !done ? 'active' : ''}`}>
                <div className={`exp1-step-number ${done ? 'done' : active ? 'current' : ''}`}>
                  {done ? <Check size={12} /> : s.id}
                </div>
                <div className="exp1-step-content">
                  <span className={`exp1-step-title ${active ? 'highlight' : ''}`}>{s.title}</span>
                  {active && !done && <span className="exp1-step-hint">{s.short}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lab Bench */}
      <div className="exp1-lab-bench">
        <div className="exp1-bench-header">
          <div className="exp1-bench-title"><FlaskConical size={16} /><h3>Lab Bench</h3></div>
          <span className="exp1-bench-progress">{completedCount}/{steps.length} Complete</span>
        </div>
        <div className="exp1-bench-area">
      <div className="emission-setup">
        {/* Safety Warning */}
        {!safetyAcknowledged && (
          <div className="safety-warning-panel">
            <div className="warning-header">
              <AlertTriangle size={24} />
              <h3>Safety Warning - High Voltage Equipment</h3>
            </div>
            <ul className="safety-list">
              <li>This experiment uses <strong>high voltage (5000V)</strong> power supplies</li>
              <li><strong>Never touch</strong> the discharge tubes or connections while powered</li>
              <li><strong>LAMP MAY BECOME HOT</strong> during use - allow it to cool for a few minutes before switching it out</li>
              <li><strong>ONLY handle the bulbs by the larger ends</strong>, not the slim centre piece - they are somewhat fragile</li>
              <li>Do not look directly at powered tubes for <strong>extended periods</strong> - UV radiation may be emitted</li>
              <li>Lift spectrometer using the <strong>base only</strong>, not the arms - keep it level in transport</li>
            </ul>
            <div className="btn-row">
              <button className="acknowledge-btn" onClick={handleAcknowledgeSafety}>
                <Check size={16} />
                I Understand and Acknowledge
              </button>
              {stepInfoContent[1] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(1); }}
                >
                  ?
                </button>
              )}
            </div>
          </div>
        )}

        {/* Gas Discharge Setup */}
        {safetyAcknowledged && (
          <div className="equipment-area">
            <GasDischargeSetup
              selectedTube={selectedGas}
              isPowered={isPowered}
              onSelectTube={handleSelectTube}
              onTogglePower={handleTogglePower}
            />

            {/* Instructions */}
            <div className="instructions-panel">
              {!selectedGas && (
                <div className="instruction">
                  <div className="instruction-header">
                    <span className="step-badge">Step 2</span>
                    {stepInfoContent[2] && (
                      <button
                        className="step-info-btn"
                        onClick={(e) => { e.stopPropagation(); setShowInfo(2); }}
                      >
                        ?
                      </button>
                    )}
                  </div>
                  <p>Select the <strong>Hydrogen</strong> discharge tube from the rack.</p>
                  <p className="hint">Click on the hydrogen (H) tube to select it.</p>
                </div>
              )}

              {selectedGas && !isPowered && (
                <div className="instruction">
                  <div className="instruction-header">
                    <span className="step-badge">Step 3</span>
                    {stepInfoContent[3] && (
                      <button
                        className="step-info-btn"
                        onClick={(e) => { e.stopPropagation(); setShowInfo(3); }}
                      >
                        ?
                      </button>
                    )}
                  </div>
                  <p>Power on the discharge tube to observe the emission.</p>
                  <p className="hint">Click the power button on the power supply.</p>
                </div>
              )}

              {isPowered && (
                <div className="instruction success">
                  <Check size={20} />
                  <div>
                    <p><strong>Setup Complete!</strong></p>
                    <p>The {selectedGas} tube is now glowing. You can proceed to measure the emission spectrum.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Tube Glow Info */}
            {isPowered && (
              <div className="glow-info">
                <Zap size={16} />
                <span>
                  {selectedGas === 'hydrogen' && 'Hydrogen emits a pink-purple glow due to the Balmer series emissions'}
                  {selectedGas === 'helium' && 'Helium emits a yellow-orange glow from multiple spectral lines'}
                  {selectedGas === 'neon' && 'Neon emits an orange-red glow characteristic of neon signs'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Theory Box */}
        <div className="theory-box">
          <h4>Why Do Gases Glow?</h4>
          <p>
            When high voltage is applied to a gas at low pressure, electrons gain energy and
            excite the gas atoms. As excited electrons fall back to lower energy levels, they
            emit photons of specific wavelengths - creating the characteristic colored light
            unique to each element.
          </p>
          <div className="equation">
            E = h&#x1D708; = hc/&#x03BB;
          </div>
        </div>

        <style>{`
          .exp1-part-layout { display: flex; gap: 16px; flex: 1; width: 100%; min-height: 0; padding: 16px; }
          .exp1-steps-panel { width: 240px; background: var(--bg-surface); border-radius: 12px; border: 1px solid var(--border); display: flex; flex-direction: column; overflow: hidden; }
          .exp1-steps-header { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); padding: 14px 16px; display: flex; align-items: center; gap: 10px; color: white; }
          .exp1-steps-header h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp1-steps-list { flex: 1; overflow-y: auto; padding: 12px; }
          .exp1-step-item { display: flex; align-items: flex-start; gap: 12px; padding: 8px 10px; border-radius: 8px; margin-bottom: 2px; }
          .exp1-step-item.active { background: rgba(255, 90, 54, 0.15); }
          .exp1-step-number { width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; flex-shrink: 0; background: var(--border); color: var(--text-muted); }
          .exp1-step-number.done { background: var(--secondary); color: white; }
          .exp1-step-number.current { background: var(--primary); color: white; }
          .exp1-step-content { flex: 1; min-width: 0; }
          .exp1-step-title { font-size: 12px; font-weight: 600; display: block; }
          .exp1-step-title.highlight { color: var(--primary-light); }
          .exp1-step-hint { font-size: 11px; color: var(--text-secondary); margin-top: 4px; display: block; }
          .exp1-lab-bench { flex: 1; background: var(--bg-surface); border-radius: 12px; border: 1px solid var(--border); display: flex; flex-direction: column; overflow: auto; }
          .exp1-bench-header { background: linear-gradient(135deg, var(--secondary), var(--secondary-dark)); padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; color: white; }
          .exp1-bench-title { display: flex; align-items: center; gap: 10px; }
          .exp1-bench-title h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp1-bench-progress { font-size: 12px; opacity: 0.9; }
          .exp1-bench-area { flex: 1; padding: 20px; overflow-y: auto; }

          .emission-setup {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            width: 100%;
            max-width: 600px;
          }

          .safety-warning-panel {
            width: 100%;
            background: rgba(255, 90, 54, 0.1);
            border: 2px solid #FF5A36;
            border-radius: 12px;
            padding: 20px;
          }

          .warning-header {
            display: flex;
            align-items: center;
            gap: 12px;
            color: #FF5A36;
            margin-bottom: 16px;
          }

          .warning-header h3 {
            margin: 0;
            font-size: 16px;
          }

          .safety-list {
            margin: 0 0 20px 0;
            padding-left: 24px;
            font-size: 13px;
            color: var(--text-primary);
            line-height: 1.8;
          }

          .safety-list strong {
            color: #FF5A36;
          }

          .acknowledge-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 24px;
            background: #FF5A36;
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .acknowledge-btn:hover {
            background: #E04A26;
            transform: translateY(-2px);
          }

          .equipment-area {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
            width: 100%;
          }

          .instructions-panel {
            width: 100%;
          }

          .instruction {
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
            border-left: 4px solid var(--primary);
          }

          .instruction.success {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            border-left-color: #00A86B;
            color: #00A86B;
          }

          .instruction.success p {
            margin: 0;
            color: var(--text-primary);
          }

          .instruction.success p:first-child {
            color: #00A86B;
          }

          .step-badge {
            display: inline-block;
            background: var(--primary);
            color: white;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            margin-bottom: 8px;
          }

          .instruction p {
            margin: 0 0 4px 0;
            font-size: 13px;
            color: var(--text-primary);
          }

          .instruction .hint {
            font-size: 12px;
            color: var(--text-muted);
            font-style: italic;
          }

          .glow-info {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 16px;
            background: rgba(255, 193, 7, 0.1);
            border-radius: 8px;
            font-size: 12px;
            color: #FFC107;
          }

          .theory-box {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .theory-box p {
            margin: 0 0 12px 0;
            font-size: 12px;
            color: var(--text-primary);
            line-height: 1.6;
          }

          .theory-box .equation {
            text-align: center;
            font-size: 14px;
            font-family: 'Times New Roman', serif;
            color: var(--text-secondary);
            background: var(--bg-dark);
            padding: 8px;
            border-radius: 4px;
          }
        .btn-row {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .instruction-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp1"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
        </div>
      </div>
    </div>
  );
}
